/**
 * Radial chromatic aberration — edges only, and very restrained.
 *
 * Real lens dispersion is zero on the optical axis and grows toward the corners,
 * so the displacement here is driven by the squared distance from the centre of
 * the *filter area* and shaped by an exponent. That means the cards around the
 * middle of the table — where the player is reading numbers — stay perfectly
 * clean, and only the rail and the outer wells pick up the fringe.
 *
 * Displacement is expressed in frame-relative units rather than pixels, so it
 * looks identical at 1920x1080 and on a phone.
 */

import { Filter, GlProgram, defaultFilterVert } from 'pixi.js';
import type { FilterSystem, RenderSurface, Texture } from 'pixi.js';

import type { CardShield, ShieldUniforms } from './CardShield';
import { GLSL_SHIELD, shieldResources } from './CardShield';
import { GLSL_FRAME, GLSL_GLOBALS } from './glsl';

const fragment = /* glsl */ `
in vec2 vTextureCoord;
out vec4 finalColor;

uniform sampler2D uTexture;
${GLSL_GLOBALS}
uniform float uStrength;
uniform float uFalloff;

${GLSL_FRAME}
${GLSL_SHIELD}

void main(void) {
    float aspect = frameAspect();

    // Aspect-corrected, so the ramp is a circle around the optical axis rather
    // than an ellipse stretched with the window.
    vec2 p = (frameUv(vTextureCoord) - 0.5) * vec2(aspect, 1.0);

    // 0 on the axis, 1 in the corners. The exponent keeps the middle of the
    // table — where the totals are read — completely clean.
    float maxRadius = length(vec2(aspect, 1.0)) * 0.5;
    float radial = clamp(length(p) / maxRadius, 0.0, 1.0);
    float amount = pow(radial, uFalloff) * uStrength;

    // Undo the aspect correction and scale back into texture space, so the
    // displacement is the same number of pixels in every direction.
    vec2 direction = p / max(length(p), 1e-5);
    vec2 offset = direction * amount * vec2(1.0 / aspect, 1.0) * frameScale();

    // Five taps, not three.
    //
    // Real lateral dispersion is continuous — focal length varies smoothly with
    // wavelength, so a white edge smears into a continuous rainbow. Sampling
    // exactly three points at +d, 0, -d gives three hard-edged primary copies,
    // and at the peak displacement here (about 2.4 device px) the red and blue
    // copies land ~4.8px apart: well past where the eye resolves them as two
    // separate coloured lines rather than as a fringe. On the gold rail
    // hairlines at the frame edge that showed as a distinct red line and a
    // distinct blue line.
    //
    // The peak displacement is unchanged — this is about tap density, not
    // amplitude.
    vec4 cg = texture(uTexture, vTextureCoord);
    vec3 acc = vec3(0.0);
    vec3 wsum = vec3(0.0);
    for (int i = 0; i < 5; i++) {
        float t = float(i) * 0.5 - 1.0;
        vec3 w = vec3(max(0.0, t), 1.0 - abs(t), max(0.0, -t));
        acc += texture(uTexture, clampUv(vTextureCoord + offset * t)).rgb * w;
        wsum += w;
    }

    float a = cg.a;
    vec4 fringed = vec4(min(acc / max(wsum, vec3(1e-4)), vec3(a)), a);

    // A card is not seen through the lens: its pixels come through as given,
    // not resampled (five taps at a sub-pixel offset are a blur, however small
    // the offset). See CardShield.
    finalColor = mix(fringed, cg, shielded(vTextureCoord));
}
`;

export interface ChromaticAberrationFilterOptions {
  /**
   * Peak displacement as a fraction of the frame, reached at the corners.
   * 0.002 is roughly two pixels at 1080p — which is already plenty.
   */
  strength?: number;
  /** Exponent on the radial ramp. Higher = tighter to the edges. */
  falloff?: number;
}

const DEFAULTS: Required<ChromaticAberrationFilterOptions> = {
  strength: 0.0022,
  falloff: 1.7,
};

interface AberrationUniforms {
  uStrength: number;
  uFalloff: number;
}

export class ChromaticAberrationFilter extends Filter {
  readonly #uniforms: AberrationUniforms;
  readonly #shieldUniforms: ShieldUniforms;

  /** Where the cards are, if they are to be left out. See CardShield. */
  shield: CardShield | null = null;

  constructor(options: ChromaticAberrationFilterOptions = {}) {
    const o = { ...DEFAULTS, ...options };

    super({
      glProgram: GlProgram.from({
        vertex: defaultFilterVert,
        fragment,
        name: 'wild21-chromatic-aberration',
        preferredFragmentPrecision: 'highp',
      }),
      resources: {
        aberrationUniforms: {
          uStrength: { value: o.strength, type: 'f32' },
          uFalloff: { value: o.falloff, type: 'f32' },
        },
        ...shieldResources(),
      },
      resolution: 'inherit',
      antialias: 'inherit',
      padding: 0,
    });

    this.#uniforms = this.resources.aberrationUniforms.uniforms as AberrationUniforms;
    this.#shieldUniforms = this.resources.shieldUniforms.uniforms as ShieldUniforms;
  }

  override apply(
    filterManager: FilterSystem,
    input: Texture,
    output: RenderSurface,
    clearMode: boolean,
  ): void {
    if (this.shield) this.shield.bind(this, this.#shieldUniforms, input);
    else this.#shieldUniforms.uShield = 0;
    filterManager.applyFilter(this, input, output, clearMode);
  }

  /** Peak corner displacement, as a fraction of the frame width. */
  get strength(): number {
    return this.#uniforms.uStrength;
  }
  set strength(value: number) {
    this.#uniforms.uStrength = Math.max(0, value);
  }

  /** Exponent on the centre-to-edge ramp. */
  get falloff(): number {
    return this.#uniforms.uFalloff;
  }
  set falloff(value: number) {
    this.#uniforms.uFalloff = Math.max(0.01, value);
  }
}
